import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { take, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AUTH_STATE } from '../tokens/auth-state.token';
import { GET_USER_STATUS } from '../tokens/user-status.token';
import { computeEffectiveStatus } from '../models/user-status.model';
import { foreignRoleRedirect } from './role-areas';

/**
 * Guards /collaborators y /collaborators/:id — la nómina del socio empleador.
 *
 * Ojo con el plural: `/collaborator` (singular, `collaboratorGuard`) es el área del
 * colaborador mismo; `/collaborators` es la de su empleador. Un colaborador que
 * llegue acá se va a su propia área por la tabla de `role-areas.ts`, no por una
 * rama escrita a mano.
 *
 * - Anonymous users → /
 * - Managers → /manager
 * - Colaboradores → /collaborator
 * - Socios con estado distinto de "approved" → /home (invitar exige alojamiento
 *   activo, que el backend vuelve a verificar en `loadEmployerContext`)
 * - Approved socios → allow
 */
export const collaboratorsGuard: CanActivateFn = () => {
  const authState$ = inject(AUTH_STATE);
  const getUserStatus = inject(GET_USER_STATUS);
  const router = inject(Router);

  return authState$.pipe(
    take(1),
    switchMap((user) => {
      if (!user) return [router.createUrlTree(['/'])];
      return getUserStatus(user.uid).pipe(
        map((status) => {
          if (!status) return router.createUrlTree(['/home']);
          const away = foreignRoleRedirect(router, status.role, ['socio']);
          if (away) return away;
          const effective = computeEffectiveStatus(
            status.registrationStatus,
            user.emailVerified,
          );
          if (effective !== 'approved') {
            return router.createUrlTree(['/home']);
          }
          return true;
        }),
      );
    }),
  );
};
